import React from 'react';
import { Helmet } from 'react-helmet-async';

const APP_STORE_URL = 'https://apps.apple.com/us/app/foodlens-ai/id6744621676';

const features = [
  {
    name: 'Allergen Detection',
    description: 'Detect nuts, soy, egg, dairy, and gluten instantly. Free allergen scanning for the conditions that matter most.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
  },
  {
    name: 'Medical Safety Lenses',
    description: 'Purpose-built lenses for celiac disease, PKU, G6PD deficiency, and histamine intolerance. Always free.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
    ),
  },
  {
    name: 'Seed Oils & Additives',
    description: 'Identify seed oils, artificial colors, preservatives, gums, and ultra-processed markers in any product.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  },
  {
    name: 'Custom Lenses',
    description: 'Create personalized lenses to track any ingredients that matter to your diet or health needs.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
  },
  {
    name: 'Barcode Scanning',
    description: 'Scan product barcodes for instant ingredient analysis from our growing database.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
      </svg>
    ),
  },
  {
    name: 'Scan History',
    description: 'Keep track of products you\'ve scanned and quickly reference your results anytime.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const tiers = [
  {
    name: 'Free',
    price: '$0',
    description: 'All allergen, medical, and health lenses included. No paywall for safety.',
    features: [
      '15+ detection lenses',
      'All allergen lenses (Nuts, Soy, Egg, Dairy)',
      'All medical lenses (Celiac, PKU, G6PD)',
      'Seed oils, preservatives, nitrates & more',
      'Barcode scanning',
      'Scan history (10 items)',
      '100% Ad-free experience',
    ],
    cta: 'Download Free',
    highlighted: false,
  },
  {
    name: 'Premium',
    price: '$0.99',
    frequency: '/month',
    yearlyPrice: '$9.99/year',
    description: 'Custom lenses, artificial sweetener detection, and power-user features.',
    features: [
      'All Free features',
      'Artificial Sweeteners Lens',
      'Custom Lens creation',
      'Unlimited scan history',
      '1.5x points earning multiplier',
      'Priority support',
    ],
    cta: 'Start Free Trial',
    highlighted: true,
  },
];

function AppPage() {
  return (
    <>
      <Helmet>
        <title>FoodLens iOS App - Allergen & Ingredient Scanner | FoodLens AI</title>
        <meta name="description" content="Scan ingredient labels for allergens, celiac, PKU, seed oils, and 15+ dietary concerns. Free allergen and medical condition detection. No login required." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-hero noise-overlay py-20 md:py-28">
        <div className="absolute top-20 right-20 w-96 h-96 bg-sage-200 rounded-full blur-3xl opacity-30" />

        <div className="section relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-sage-100 text-sage-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                iOS App
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-tight mb-6">
                Allergen & Ingredient{' '}
                <span className="text-sage-500">Safety Scanner</span>
              </h1>

              <p className="text-lg sm:text-xl text-gray-500 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0">
                Point your camera at any ingredient list. FoodLens detects allergens, gluten, seed oils, and 15+ dietary concerns in 3 seconds. Free for every medical and allergen lens.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary text-lg px-8 py-4 gap-2"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  Download on App Store
                </a>
              </div>
            </div>

            <div className="relative flex justify-center">
              <div className="relative animate-float">
                <img
                  src="/images/hero-phone.png"
                  alt="FoodLens iOS app"
                  className="w-72 lg:w-80 drop-shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="bg-surface py-20 md:py-28">
        <div className="section">
          <div className="text-center mb-16">
            <h2 className="section-heading">Everything You Need to Shop Smarter</h2>
            <p className="section-subheading mx-auto">
              Powerful features designed to help you make informed food choices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={feature.name}
                className="card animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="inline-flex p-3 rounded-xl bg-sage-100 text-sage-600 mb-4">
                  {feature.icon}
                </div>
                <h3 className="font-display text-xl text-charcoal mb-2">{feature.name}</h3>
                <p className="text-gray-500 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot Gallery */}
      <section className="bg-background py-20 md:py-28">
        <div className="section">
          <div className="text-center mb-16">
            <h2 className="section-heading">See It In Action</h2>
            <p className="section-subheading mx-auto">
              A closer look at the FoodLens scanning experience.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { src: '/images/pic1.webp', alt: 'FoodLens app screenshot 1' },
              { src: '/images/pic2.webp', alt: 'FoodLens app screenshot 2' },
              { src: '/images/pic3.webp', alt: 'FoodLens app screenshot 3' },
              { src: '/images/pic4.webp', alt: 'FoodLens app screenshot 4' },
            ].map((screenshot, index) => (
              <div
                key={screenshot.alt}
                className="bg-white rounded-2xl p-3 md:p-4 shadow-lg animate-fade-in-up"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <img
                  src={screenshot.src}
                  alt={screenshot.alt}
                  className="w-full h-auto rounded-xl"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="bg-surface py-20 md:py-28">
        <div className="section">
          <div className="text-center mb-16">
            <h2 className="section-heading">Simple, Transparent Pricing</h2>
            <p className="section-subheading mx-auto">
              Start free and upgrade when you're ready for more features.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {tiers.map((tier, index) => (
              <div
                key={tier.name}
                className={`relative rounded-3xl p-8 ${
                  tier.highlighted
                    ? 'bg-sage-500 text-white ring-4 ring-sage-300'
                    : 'bg-surface border-2 border-sage-100'
                } shadow-elevated animate-fade-in-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-coral-500 text-white text-sm font-bold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <h3 className={`font-display text-2xl ${tier.highlighted ? 'text-white' : 'text-charcoal'} mb-2`}>
                  {tier.name}
                </h3>

                <div className="flex items-baseline gap-1 mb-2">
                  <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-charcoal'}`}>
                    {tier.price}
                  </span>
                  {tier.frequency && (
                    <span className={tier.highlighted ? 'text-white/80' : 'text-gray-500'}>
                      {tier.frequency}
                    </span>
                  )}
                </div>

                {tier.yearlyPrice && (
                  <p className={`text-sm mb-4 ${tier.highlighted ? 'text-white/80' : 'text-gray-500'}`}>
                    or {tier.yearlyPrice}
                  </p>
                )}

                <p className={`mb-6 ${tier.highlighted ? 'text-white/90' : 'text-gray-500'}`}>
                  {tier.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <svg
                        className={`w-5 h-5 mt-0.5 flex-shrink-0 ${tier.highlighted ? 'text-white' : 'text-sage-500'}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className={tier.highlighted ? 'text-white/90' : 'text-gray-500'}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block w-full text-center py-3 px-6 rounded-xl font-medium transition-all ${
                    tier.highlighted
                      ? 'bg-white text-sage-600 hover:bg-sage-50'
                      : 'bg-sage-500 text-white hover:bg-sage-600'
                  }`}
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Points/Rewards System */}
      <section className="bg-gradient-sage text-white py-20 md:py-28">
        <div className="section">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl text-white mb-6">
                Earn Real Cash Rewards
              </h2>
              <p className="text-xl text-white/80 mb-8">
                Share in our success! Every quarter, we distribute 10% of profits to users based on points earned.
              </p>

              <div className="space-y-6">
                {[
                  { title: 'Quarterly Payouts', desc: 'Rewards distributed every three months via Venmo or gift cards.' },
                  { title: '10% Profit Sharing', desc: 'Your reward is proportional to your points vs total points earned.' },
                  { title: '1.5x Premium Multiplier', desc: 'Premium users earn 50% more points on every action.' },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">{item.title}</h3>
                      <p className="text-white/70">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
              <h3 className="font-display text-2xl text-white mb-4">Example Payout</h3>
              <div className="space-y-4">
                {[
                  { label: 'Quarterly profit', value: '$10,000' },
                  { label: 'Reward pool (10%)', value: '$1,000' },
                  { label: 'Your points', value: '500 / 5,000 total' },
                  { label: 'Your reward', value: '$100', highlight: true },
                ].map((row) => (
                  <div
                    key={row.label}
                    className={`flex justify-between items-center py-3 ${
                      row.highlight ? 'border-t border-white/20 pt-4 mt-4' : ''
                    }`}
                  >
                    <span className="text-white/80">{row.label}</span>
                    <span className={`font-semibold ${row.highlight ? 'text-2xl' : ''}`}>{row.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-background py-20">
        <div className="section text-center">
          <h2 className="section-heading mb-4">Ready to Shop Smarter?</h2>
          <p className="section-subheading mx-auto mb-8">
            Download FoodLens and start making informed food choices today.
          </p>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-lg px-8 py-4 gap-2"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            Download on App Store
          </a>
        </div>
      </section>
    </>
  );
}

export default AppPage;
